import { makeObservable, action, observable } from 'mobx';
import { BaseService } from '../base';
import { AgreementResponseModel } from './model';
import AgreementID, { IAppendixInfo } from './interface.id';
import { IActivationContractInfo, IContractInfo } from './interface';
import { locale } from '@/utils/v2/locale';

export class AgreementService extends BaseService {
  @observable agreementState: AgreementResponseModel = new AgreementResponseModel();
  // id年度放款明细附录
  @observable appendixList: IAppendixInfo[] = [];
  // 激活合同附录&主动还款附录
  @observable agreementAddendumList: IContractInfo[] = [];
  // 激活合同列表（包含旧合同、终止合同、新LDS合同）
  @observable activationContractList: IActivationContractInfo[] = [];

  constructor() {
    super();
    makeObservable(this);
  }

  @action
  async getAgreementInfo(params: any) {
    const url = AgreementID.API.get_loan_agreement_info;
    const { code, result } = await this.post(url, {
      body: { ...params },
    });
    if (code === 0) {
      this.agreementState = {
        ...this.agreementState,
        ...result,
      };
      return result || this.agreementState;
    }
  }

  @action
  async getAppendixList(params: any) {
    const { code, result } = await this.post(AgreementID.API.get_appendix_list, {
      body: { ...params },
    });
    if (code === 0) {
      const { title_list = [] } = result;
      this.appendixList = [...this.appendixList, ...title_list].sort((a, b) => a.year - b.year);
    }
  }

  @action
  clearAppendixList() {
    this.appendixList = [];
  }

  @action
  async getAppendixDetailInfo(params: any) {
    const result = await this.post(AgreementID.API.get_disbursement_details_info, {
      body: { ...params },
    });
    return result;
  }

  @action
  async getAppendixDetailPdf(params: any) {
    const result = await this.post(AgreementID.API.get_disbursement_details_pdf, {
      body: { ...params },
    });
    return result;
  }

  // USS 迁移：拿 contract_id 换 UC presign url
  // uss_switch_on=true 返回 USS 绝对 url；false 说明未切 USS，BE 原样回传 ceph 相对 url，前端需拼 origin 走老流程
  @action
  async getAppendixDetailPresignedUrl(params: { contract_id?: string; contract_url?: string }) {
    const { code, result } = await this.post(AgreementID.API.gen_presigned_url_from_uc, {
      body: {
        uc_contract_id: params.contract_id,
        uc_contract_url: params.contract_url,
      },
    });
    if (code === 0) {
      return {
        presignedUrl: result?.presigned_url || '',
        ussSwitchOn: result?.uss_switch_on,
      };
    }
    return { presignedUrl: '', ussSwitchOn: undefined };
  }

  @action
  async getAddendumInfo(params: any) {
    const { code, result } = await this.post(AgreementID.API.get_agreement_addendum_info, {
      body: { ...params },
    });
    if (code === 0) {
      const { contract_info_list = [] } = result;
      const newAddendumList = contract_info_list.map((item: any) => {
        const commonInfo = {
          contract_id: item.contract_id,
          accept_time: item.accept_time,
        };
        if (locale === 'en') {
          return {
            ...commonInfo,
            contract_url: item.contract_url_en,
            contract_url_presigned: item.contract_url_en_presigned,
            contract_picture_url: item.contract_picture_url_en,
            contract_picture_url_presigned: item.contract_picture_url_en_presigned,
          };
        }
        return {
          ...commonInfo,
          contract_url: item.contract_url_local,
          contract_url_presigned: item.contract_url_local_presigned,
          contract_picture_url: item.contract_picture_url_local,
          contract_picture_url_presigned: item.contract_picture_url_local_presigned,
        };
      });

      // 按照 accept_time 从最新到最旧排序
      const sortedAddendumList = newAddendumList.sort((a: any, b: any) => {
        return b.accept_time - a.accept_time;
      });

      this.agreementAddendumList = [...sortedAddendumList];
    }
  }

  @action
  clearAddendumContractList() {
    this.agreementAddendumList = [];
  }

  @action
  setActivationContractList(contracts: IActivationContractInfo[]) {
    this.activationContractList = contracts;
  }

  @action
  clearActivationContractList() {
    this.activationContractList = [];
  }
}
